/**
 * React Query hooks for the per-user subscription override API. Write
 * hooks invalidate the user's list — the server recomputes effective
 * values on revert, so a refetch is the only way to learn the
 * post-revert state.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clearUserSubscriptionOverride,
  getUserSubscriptions,
  setUserSubscriptionOverride,
  type UserSubscriptionOverridePayload,
} from '../../services/userSubscriptions.service';

export const userSubscriptionKeys = {
  all: ['user-subscriptions'] as const,
  /** All site-scoped lists for one user — the invalidation prefix, so a
   *  write refetches every site's list regardless of which is on screen. */
  forUser: (userId: number) => [...userSubscriptionKeys.all, userId] as const,
  /** One user's list, optionally scoped to a single site. */
  list: (userId: number, school: number | null = null) =>
    [...userSubscriptionKeys.forUser(userId), school] as const,
};

export function useUserSubscriptions(
  userId: number | null | undefined,
  /** Optional site scope — drives the `?school=` query param. */
  school?: number | null,
) {
  return useQuery({
    queryKey: userSubscriptionKeys.list(userId ?? 0, school ?? null),
    queryFn: () => getUserSubscriptions(userId as number, school ?? null),
    enabled: !!userId,
  });
}

export function useSetUserSubscriptionOverride(userId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      alertTypeId,
      body,
    }: {
      alertTypeId: number;
      body: UserSubscriptionOverridePayload;
    }) => setUserSubscriptionOverride(userId, alertTypeId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: userSubscriptionKeys.forUser(userId),
      });
    },
  });
}

export function useClearUserSubscriptionOverride(userId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ alertTypeId }: { alertTypeId: number }) =>
      clearUserSubscriptionOverride(userId, alertTypeId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: userSubscriptionKeys.forUser(userId),
      });
    },
  });
}
